import React, { memo } from 'react';
import { Box, Typography, IconButton, Chip, Button, Link } from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Close as RejectIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
} from '@mui/icons-material';
import { STATUS_CONFIG, renderVerificationStatus, downloadDocument, createFileInput } from './documentUtils';
import { getCurrentCurrencyLocale } from '../../utils/CurrencyUtils';

// Document templates that can be generated and downloaded
const DOCUMENT_TEMPLATES = {
  "Organization chart": () => {
    const csvContent = [
      "Organization Chart",
      "Please remove / rename irrelevant department(s)",
      "",
      "Department Name,Number of Employees",
      "Management,",
      "Business development & sales,",
      "Design,",
      "On-site workers,",
      "Engineering & design,",
      "Project management & execution,",
      "Procurement & supply chain,",
      "Finance & accounting,"
    ].join("\n");
    return { content: csvContent, filename: "organization_chart_template.csv", type: "text/csv;charset=utf-8;" };
  },
  "Questionnaire": () => {
    const csvContent = [
      "Questionnaire",
      "",
      "Question,Answer",
      "Years of operations,",
      "Number of projects (lifetime),",
      `Size of projects (lifetime) in ${getCurrentCurrencyLocale()},`,
      "Number of projects in the last 6 months,",
      `Size of projects (last 6 months) in ${getCurrentCurrencyLocale()},`,
      "Manpower,",
      "Manpower (Inhouse / Outsourced),",
      "Factories / workshops (Inhouse / Outsourced),",
      "Office size in SQM,",
      "Bank account average balance over the last 6 months,",
      "Video for office,",
      "Video for factory / workshop,",
      "ISO certificate (yes / no),",
      "Master developer (yes / no),"
    ].join("\n");
    return { content: csvContent, filename: "questionnaire_template.csv", type: "text/csv;charset=utf-8;" };
  }
};

const DocumentItem = memo(({ doc, onUpload, onDelete, readOnly = false }) => {
  const status = renderVerificationStatus(doc);
  
  // Check if this document type has a downloadable template
  const hasTemplate = doc?.sub_type && DOCUMENT_TEMPLATES[doc.sub_type];
  
  const handleDownloadTemplate = () => {
    const templateGenerator = DOCUMENT_TEMPLATES[doc?.sub_type];
    if (!templateGenerator) return;
    
    const { content, filename, type } = templateGenerator();
    const blob = new Blob([content], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onUpload(file, doc.id);
    }
  };

  const handleUploadClick = () => {
    const input = createFileInput(handleFileChange);
    input.click();
  };

  const handleDownload = () => {
    downloadDocument(doc);
  };

  const handleApprove = () => {
    console.log('Approving document:', doc.id);
    // TODO: Implement approve API call
  };

  const handleReject = () => {
    console.log('Rejecting document:', doc.id);
    // TODO: Implement reject API call
  };

  const getStatusChip = (status) => {
    if (!status) return null;
    
    const config = STATUS_CONFIG[status];
    if (!config) return null;

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          color: config.color,
          backgroundColor: config.bgcolor,
          fontWeight: 500,
          '& .MuiChip-label': {
            px: 1
          }
        }}
        icon={
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: config.color,
              ml: 0.5
            }}
          />
        }
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, md: 3 } }}>
        {/* Left side - Upload/File area */}
        <Box sx={{ minWidth: { xs: 50, md: 60 }, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {doc?.url ? (
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2,
                  backgroundColor: '#4D6BF6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(77, 107, 246, 0.2)'
                }}
                onClick={() => window.open(doc.url, '_blank')}
              >
                <DescriptionIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              {/* TEMPORARY: Delete button enabled for all documents including VERIFIED */}
              {!readOnly && doc?.url && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(doc.id);
                  }}
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    width: 28,
                    height: 28,
                    backgroundColor: '#EF4444',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(239, 68, 68, 0.4)',
                    zIndex: 10,
                    '&:hover': {
                      backgroundColor: '#DC2626',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: 'white', fontWeight: 'bold' }} />
                </IconButton>
              )}
            </Box>
          ) : (
            <Box
              onClick={readOnly ? undefined : handleUploadClick}
              sx={{
                width: 50,
                height: 50,
                borderRadius: 2,
                backgroundColor: '#F8F9FA',
                border: '2px dashed #D1D5DB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: readOnly ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: readOnly ? 0.5 : 1,
                '&:hover': readOnly ? {} : {
                  backgroundColor: '#F0F4FF',
                  borderColor: '#4D6BF6'
                }
              }}
            >
              <AddIcon sx={{ color: '#6B7280', fontSize: 24 }} />
            </Box>
          )}
          
          {/* Status indicator below icon */}
          {status && (
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: status === 'approved' ? '#10B981' : 
                             status === 'rejected' ? '#EF4444' : 
                             status === 'submitted' ? '#3B82F6' : '#F59E0B'
            }} />
          )}
        </Box>

        {/* Right side - Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ 
                fontWeight: 500, 
                color: '#111827'
              }}>
                {doc?.sub_type}
                {doc?.is_mandatory && (
                  <Typography component="span" sx={{ color: '#EF4444', ml: 0.5 }}>
                    *
                  </Typography>
                )}
              </Typography>
            </Box>
            {/* Download button positioned at the right end */}
            {doc?.url && (
              <IconButton
                onClick={handleDownload}
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  '&:hover': {
                    backgroundColor: '#F1F5F9',
                    borderColor: '#3B82F6'
                  }
                }}
              >
                <DownloadIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
              </IconButton>
            )}
          </Box>

          {/* File info or upload prompt */}
          <Typography variant="body2" sx={{ 
            color: '#6B7280', 
            mb: 1
          }}>
            {doc?.url ? `${doc.sub_type} File` : 'No file uploaded'}
            {/* Download template link for documents that have templates */}
            {!doc?.url && hasTemplate && (
              <>
                {' - '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleDownloadTemplate}
                  sx={{
                    color: '#4D6BF6',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 500,
                    '&:hover': {
                      color: '#3B5BDB'
                    }
                  }}
                >
                  Download Template
                </Link>
              </>
            )}
          </Typography>

          {/* Upload date */}
          {doc?.url && doc?.updated_at && (
            <Typography variant="caption" sx={{ 
              color: '#9CA3AF',
              fontSize: '0.75rem',
              display: 'block',
              mb: 1
            }}>
              Submitted: {new Date(doc.updated_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </Typography>
          )}

          {/* Status chip */}
          {status && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {getStatusChip(status)}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

DocumentItem.displayName = 'DocumentItem';

export default DocumentItem;
